// Box field CRUD + the CanvasBox action-dispatch table, extracted out of
// FlowchartBuilder.tsx's component body. Pure re-wiring — every function body,
// comment, and dependency array below is moved verbatim; only parameterized.
//
// Scope note: this is a first slice, not the whole "box CRUD" surface. The
// handleContextMenu* family (toggle 24/7, toggle 24/5, toggle milestone, copy,
// duplicate, open resize-note, delete-selection, open start-constraint, open
// auto-end, plus their Save* counterparts), copySelectedBoxes/pasteBoxes,
// addStartDay, and the Areas feature all still live in FlowchartBuilder.tsx —
// deliberately deferred to a later pass, not overlooked. This slice covers
// exactly what CanvasBox's onAction dispatcher needs: the direct per-box field
// edits (title/duration/assignee/color/status/completion/resourceHours/delete)
// and the connect-handle-triggered arrow creation (startConn/completeConn).
import { useCallback, useRef } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import type { CanvasBoxAction, CanvasBoxActionArgs } from './CanvasBox';
import { wouldCreateCycle } from './lib/arrowRouting';
import type { Box, Arrow, BoxStatus, ConnectionSide, ConnectingState } from './types/index';
import type { Translations } from './i18n/types';

interface UseBoxActionsParams {
  canEditFlowchart: boolean;
  t: (key: keyof Translations, vars?: Record<string, string | number>) => string;

  boxes: Box[];
  setBoxes: Dispatch<SetStateAction<Box[]>>;
  arrows: Arrow[];
  setArrows: Dispatch<SetStateAction<Arrow[]>>;
  nextId: number;
  setNextId: Dispatch<SetStateAction<number>>;

  selectedBoxes: number[];
  setSelectedBoxes: Dispatch<SetStateAction<number[]>>;
  setHoveredBox: Dispatch<SetStateAction<number | null>>;
  setDragging: Dispatch<SetStateAction<number | null>>;
  setIsEditingBoxTitle: Dispatch<SetStateAction<number | null>>;
  setIsEditingText: Dispatch<SetStateAction<boolean>>;
  setContextMenu: Dispatch<SetStateAction<{ x: number; y: number; type: 'box' | 'canvas'; boxId?: number } | null>>;
  setLinkPanel: Dispatch<SetStateAction<{ boxId: number } | null>>;
  editingDuration: { id: number; value: string } | null;
  setEditingDuration: Dispatch<SetStateAction<{ id: number; value: string } | null>>;
  workDayHoursPerDay: number;

  connecting: ConnectingState | null;
  setConnecting: Dispatch<SetStateAction<ConnectingState | null>>;
  rightMouseDownPos: MutableRefObject<{ x: number; y: number } | null>;

  handleDown: (e: React.MouseEvent | React.TouchEvent, box: Box) => void;
  handleOpenStartConstraint: (boxId: number) => void;
  handleContextMenuOpenAutoEnd: (boxId: number) => void;
  // Opens the Subitems panel — same function the box's right-click "Edit subitems"
  // menu item already calls, just also reachable directly off the subitem badge now.
  handleOpenSubitems: (boxId: number) => void;

  // Truthy while a canvas pan is in progress (useCanvasPointerEvents.ts's own
  // isPanningRef, passed through by reference) — checked, not typed precisely, since
  // mouseEnter/mouseLeave below only need a boolean-ish truthiness check, not the
  // pan gesture's actual start-position payload.
  isPanningRef: MutableRefObject<unknown>;

  // From useUndoRedoHistory.ts — every DISCRETE action below (delete, color/status/
  // completion change, arrow creation, resource-hours toggle, duration commit on
  // blur) calls this immediately with the final boxes/arrows so it becomes its own
  // undo step, instead of leaning on the debounced autosave (which only continuous
  // edits like titleChange/assigneeChange/resizeMove still rely on).
  pushHistoryEntry: (newBoxes: Box[], newArrows: Arrow[]) => void;
}

export function useBoxActions(params: UseBoxActionsParams) {
  const {
    canEditFlowchart, t,
    boxes, setBoxes, arrows, setArrows, nextId, setNextId,
    selectedBoxes, setSelectedBoxes, setHoveredBox, setDragging,
    setIsEditingBoxTitle, setIsEditingText, setContextMenu, setLinkPanel,
    editingDuration, setEditingDuration, workDayHoursPerDay,
    connecting, setConnecting, rightMouseDownPos,
    handleDown, handleOpenStartConstraint, handleContextMenuOpenAutoEnd, handleOpenSubitems,
    isPanningRef, pushHistoryEntry,
  } = params;

  const completionPrevRef = useRef<Map<number, number>>(new Map());

  /**
   * Deletes a box and all its connected arrows
   * @param id - Box ID to delete
   */
  const deleteBox = (id: number): void => {
    if (!canEditFlowchart) {
      alert(t('needEditorAccessDeleteBoxes'));
      return;
    }
    if (connecting?.boxId === id) setConnecting(null);
    // Cascade to subitems (see Box.parentBoxId) — otherwise they'd become orphaned,
    // permanently invisible dead data (never rendered, never scheduled, but never
    // cleaned up either since nothing else references them by parentBoxId anymore).
    const removedIds = new Set([id, ...boxes.filter(b => b.parentBoxId === id).map(b => b.id)]);
    const newBoxes = boxes.filter(b => !removedIds.has(b.id));
    const newArrows = arrows.filter(a => !removedIds.has(a.fromBox) && !removedIds.has(a.toBox));
    pushHistoryEntry(newBoxes, newArrows);
    setBoxes(newBoxes);
    setArrows(newArrows);
  };

  /**
   * Updates a box's title
   * @param id - Box ID
   * @param title - New title text
   */
  const updateTitle = (id: number, title: string): void => {
    if (!canEditFlowchart) return;
    // Functional form deliberately — CanvasBox.tsx's title-textarea onChange can
    // call this AND onResizeMove back to back in the same handler (when text
    // overflows and the box auto-grows); a stale `boxes` snapshot here used to
    // silently discard whichever of the two ran first. Reading `prev` instead
    // makes the result correct regardless of call order.
    setBoxes(prev => prev.map(b => b.id === id ? { ...b, title, text: title } : b));
  };

  /**
   * Updates a box's duration
   * @param id - Box ID
   * @param duration - Duration string (e.g., "5 days")
   */
  const updateDur = (id: number, duration: string): void => {
    if (!canEditFlowchart) return;
    if (duration !== '' && !/^\d+(?:\.\d*)?[hdwHDW]?$/.test(duration)) return; // accept e.g. "5d", "2.5h", "1.5w"
    // Only ever called from durationBlur — a discrete, one-shot commit (not a
    // per-keystroke update like updateTitle/updateAssign above), so it pushes
    // immediately rather than leaning on the debounce. Kept on the explicit
    // `boxes` snapshot (not the functional form) deliberately: pushHistoryEntry
    // needs the synchronous final array, and durationBlur never pairs this with
    // another setBoxes call in the same handler the way titleChange/resizeMove
    // can — no known staleness risk here to harden against.
    const newBoxes = boxes.map(b => b.id === id ? { ...b, duration } : b);
    pushHistoryEntry(newBoxes, arrows);
    setBoxes(newBoxes);
  };

  /**
   * Updates a box's assignee
   * @param id - Box ID
   * @param assignee - Assignee name
   */
  const updateAssign = (id: number, assignee: string): void => {
    if (!canEditFlowchart) return;
    // Functional form — same staleness hazard as updateTitle above, kept
    // consistent even though no currently-known caller pairs this with another
    // setBoxes call in the same handler.
    setBoxes(prev => prev.map(b => b.id === id ? { ...b, assignee } : b));
  };

  // Per-resource active-hours override within a task's duration (Gantt "select specific
  // resource times"). `hours: null` clears the override — resource is active the whole
  // task again, matching the pre-feature default.
  const updateResourceHours = (id: number, resource: string, hours: boolean[] | null): void => {
    if (!canEditFlowchart) return;
    const newBoxes = boxes.map(b => {
      if (b.id !== id) return b;
      const next = { ...(b.resourceHours || {}) };
      if (hours) next[resource] = hours; else delete next[resource];
      return { ...b, resourceHours: next };
    });
    pushHistoryEntry(newBoxes, arrows);
    setBoxes(newBoxes);
  };

  /**
   * Sets a box's department color
   * @param id - Box ID
   * @param color - Hex color code
   */
  const setBoxColor = (id: number, color: string): void => {
    if (!canEditFlowchart) return;
    const newBoxes = boxes.map(b => b.id === id ? { ...b, departmentColor: color } : b);
    pushHistoryEntry(newBoxes, arrows);
    setBoxes(newBoxes);
  };

  /**
   * Cycles through box status (none → attention → waiting → none)
   * @param id - Box ID
   */
  const cycleStatus = (id: number): void => {
    if (!canEditFlowchart) return;
    const st: BoxStatus[] = ['none', 'blocked', 'on-hold'];
    const newBoxes = boxes.map(b => {
      if (b.id === id) {
        const i = st.indexOf(b.status || 'none');
        return { ...b, status: st[(i + 1) % st.length] };
      }
      return b;
    });
    pushHistoryEntry(newBoxes, arrows);
    setBoxes(newBoxes);
  };

  /**
   * Cycles through completion levels (none → done → 25% → 50% → 75% → done → none)
   * Uses a prev-value ref to distinguish the two 100% states in the cycle.
   * @param id - Box ID
   */
  const cycleCompletion = (id: number): void => {
    if (!canEditFlowchart) return;
    const newBoxes = boxes.map(b => {
      if (b.id !== id) return b;
      const cur = b.completion || 0;
      const prev = completionPrevRef.current.get(id) ?? -1;
      let next: number;
      if (cur === 0) next = 100;
      else if (cur === 100) next = prev === 75 ? 0 : 25;
      else if (cur === 25) next = 50;
      else if (cur === 50) next = 75;
      else if (cur === 75) next = 100;
      else next = 0;
      completionPrevRef.current.set(id, cur);
      return { ...b, completion: next };
    });
    pushHistoryEntry(newBoxes, arrows);
    setBoxes(newBoxes);
  };

  /**
   * Starts creating a connection from a box side
   * @param boxId - Box ID to connect from
   * @param side - Side of the box to connect from
   */
  const startConn = (boxId: number, side: ConnectionSide): void => {
    if (!canEditFlowchart) {
      alert(t('needEditorAccessConnections'));
      return;
    }
    setConnecting({ boxId, side });
  };

  /**
   * Completes a connection to a target box
   * @param toId - Target box ID
   * @param toSide - Target box side
   */
  const completeConn = useCallback((toId: number, toSide: ConnectionSide): void => {
    if (!canEditFlowchart) {
      alert(t('needEditorAccessConnections'));
      setConnecting(null);
      return;
    }
    if (connecting && connecting.boxId !== toId) {
      const fromBox = boxes.find(b => b.id === connecting.boxId);
      const toBox = boxes.find(b => b.id === toId);
      // Check if either box is a callout to make the line dashed
      const isDashed = !!((fromBox && fromBox.type === 'callout') || (toBox && toBox.type === 'callout'));

      if (wouldCreateCycle(connecting.boxId, toId, arrows)) {
        alert(t('circularReference'));
        setConnecting(null);
        return;
      }

      const newArrows = [...arrows, {
        fromBox: connecting.boxId,
        fromSide: connecting.side,
        toBox: toId,
        toSide,
        id: nextId,  // Use nextId instead of Date.now()
        dashed: isDashed
      }];
      pushHistoryEntry(boxes, newArrows);
      setArrows(newArrows);

      setNextId(nextId + 1);  // Increment for next arrow
    }
    setConnecting(null);
  }, [connecting, boxes, arrows, nextId, canEditFlowchart, t, setArrows, setConnecting, setNextId, pushHistoryEntry]);

  // Real implementation for boxActionRef/dispatchBoxAction. Reassigned on every render
  // so it always sees current state; the `box` argument is always the one CanvasBox
  // last rendered with, which — whether or not this specific render was skipped by
  // its memo — is guaranteed to be the box's current data (see CanvasBoxProps).
  //
  // One named handler per action, each checked against its OWN entry in
  // CanvasBoxActionArgs (e.g. 'resizeMove' must get two numbers, 'titleChange' one
  // string) — see arrowActionHandlers in FlowchartBuilder.tsx for why a mapped object
  // type gets this right where a single generic function signature can't.
  // boxActionHandlers[action] below is the one place that still has to trust the
  // runtime action string matches its args; every real call site (via onAction's
  // generic signature in CanvasBox.tsx) is fully type-checked.
  const boxActionRef = useRef<(action: CanvasBoxAction, box: Box, ...args: any[]) => void>(() => {});
  const dispatchBoxAction = useCallback((action: CanvasBoxAction, box: Box, ...args: any[]) => {
    boxActionRef.current(action, box, ...args);
  }, []);

  const boxActionHandlers: { [K in CanvasBoxAction]: (box: Box, ...args: CanvasBoxActionArgs[K]) => void } = {
    pointerDown: (box, e) => handleDown(e, box),
    contextMenu: (box, e) => {
      e.preventDefault();
      e.stopPropagation();
      const down = rightMouseDownPos.current;
      if (down && (Math.abs(e.clientX - down.x) > 5 || Math.abs(e.clientY - down.y) > 5)) {
        rightMouseDownPos.current = null;
        return;
      }
      rightMouseDownPos.current = null;
      if (!selectedBoxes.includes(box.id)) setSelectedBoxes([box.id]);
      setContextMenu({ x: e.clientX, y: e.clientY, type: 'box', boxId: box.id });
    },
    doubleClick: (box, e) => {
      if (box.type === 'regular' || box.type === 'callout') {
        e.stopPropagation();
        e.preventDefault();
        setDragging(null);
        setIsEditingBoxTitle(box.id);
        const boxEl = e.currentTarget as HTMLElement;
        if (boxEl) {
          requestAnimationFrame(() => {
            const textareas = boxEl.querySelectorAll('textarea');
            if (textareas.length > 0) { textareas[0].focus(); textareas[0].select(); }
          });
        }
      }
    },
    // Skipped entirely while panning: native mouseenter/mouseleave fire on every
    // box-boundary crossing regardless of which mouse button is held, and
    // setHoveredBox re-renders the whole tree AND (via CanvasBox's own isHovered
    // prop) mounts/unmounts a real delete-button DOM node — real, layout-dirtying
    // work that was firing throughout the first second or two of every pan across
    // a dense cluster of boxes, on top of (and independent from) the isPanningRef
    // fix for the pan gesture's own state. See the FlowchartBuilder.tsx onMouseMove
    // handler's matching guard for the other half of this same fix.
    mouseEnter: (box) => { if (!isPanningRef.current) setHoveredBox(box.id); },
    mouseLeave: () => { if (!isPanningRef.current) setHoveredBox(null); },
    titleChange: (box, value) => updateTitle(box.id, value),
    // Used to also clear selection on focus (setSelectedBoxes([])) — harmless back
    // when a box's duration/assignee fields were always visible/editable in advanced
    // mode regardless of selection, but now that they're only shown either on
    // selection or via the "View all fields" toggle (see CanvasBox.tsx's
    // editableHere), clearing selection the instant the field is focused immediately
    // hid the very field the user just clicked into. isEditingText already fully
    // guards keyboard shortcuts (Delete/copy/paste/quick-connect — see
    // useKeyboardShortcuts.ts) independently of selectedBoxes, so there's nothing
    // else selection needs to be cleared FOR here.
    titleFocus: () => { setIsEditingText(true); },
    titleBlur: () => { setIsEditingText(false); setIsEditingBoxTitle(null); },
    durationChange: (box, value) => setEditingDuration({ id: box.id, value }),
    durationFocus: (box) => { setIsEditingText(true); setEditingDuration({ id: box.id, value: box.duration || '' }); },
    durationBlur: (box) => {
      if (editingDuration?.id === box.id) {
        let v = editingDuration.value;
        if (v === '' || /^\d+(?:\.\d+)?[hdwHDW]?$/.test(v)) {
          // Round to the app's minimum granularity (half an hour) in hour-space,
          // then re-express in whichever unit was typed (default 'd' when no
          // suffix is given, matching parseDuration's own default) — otherwise
          // e.g. "3.2d" at an 8h workday decodes to 25.6h, 24 minutes off the
          // nearest half hour. Same fix as "Set automatic duration" got, just for
          // manual entry, which supports the same per-box h/d/w suffixes.
          const dm = v.match(/^(\d+(?:\.\d+)?)\s*([hdwHDW]?)$/);
          if (dm) {
            const n = parseFloat(dm[1]);
            const suffix = dm[2] || '';
            const unit = suffix.toLowerCase();
            const hoursEquivalent = unit === 'h' ? n : unit === 'w' ? n * workDayHoursPerDay * 5 : n * workDayHoursPerDay;
            const roundedHours = Math.round(hoursEquivalent * 2) / 2;
            const roundedValue = unit === 'h' ? roundedHours : unit === 'w' ? roundedHours / (workDayHoursPerDay * 5) : roundedHours / workDayHoursPerDay;
            v = `${roundedValue}${suffix}`;
          }
          updateDur(box.id, v);
        }
        setEditingDuration(null);
      }
      setIsEditingText(false);
      setIsEditingBoxTitle(null);
    },
    assigneeChange: (box, value) => updateAssign(box.id, value),
    assigneeFocus: () => { setIsEditingText(true); },
    assigneeBlur: () => { setIsEditingText(false); setIsEditingBoxTitle(null); },
    delete: (box) => deleteBox(box.id),
    colorChange: (box, color) => setBoxColor(box.id, color),
    statusClick: (box) => cycleStatus(box.id),
    completionClick: (box) => cycleCompletion(box.id),
    finishBadgeClick: (box) => handleContextMenuOpenAutoEnd(box.id),
    startBadgeClick: (box) => handleOpenStartConstraint(box.id),
    subitemBadgeClick: (box) => handleOpenSubitems(box.id),
    linkClick: (box) => setLinkPanel({ boxId: box.id }),
    resizeMove: (box, width, height) => setBoxes(prev => prev.map(b => b.id === box.id ? { ...b, width, height } : b)),
    connectHandle: (box, side) => { if (connecting) completeConn(box.id, side); else startConn(box.id, side); },
  };
  boxActionRef.current = (action, box, ...args) => {
    (boxActionHandlers[action] as (box: Box, ...args: any[]) => void)(box, ...args);
  };

  return { dispatchBoxAction, deleteBox, updateResourceHours };
}
